/**
 * The signed-in session, shared by both applications.
 *
 * ## What this is not
 *
 * It is not authorization. Every protected endpoint is role-gated on the server, and this context's
 * `role` exists so the navigation shows an employee the screens they can use — not to decide what
 * they may do. Hiding a control in the client is a presentation choice that anyone can bypass with
 * the developer tools, and building the navigation around it invites the belief that it *is* the
 * control. The role matrix in `tests/acceptance/test_phase2_authorization.py` is the control.
 *
 * ## Three states, not two
 *
 * `loading` matters as much as `authenticated` and `anonymous`. On a hard refresh the page has a
 * session cookie the script cannot read, so it must ask the server before deciding what to render.
 * Treating the unresolved case as anonymous flashes the login form at an employee who is signed in,
 * and — worse — a screen that fetches on mount would fire its request against the wrong identity.
 *
 * ## Expiry is prompted, not silently patched over
 *
 * A refresh loop that runs forever turns a fifteen-minute access token into an indefinite session,
 * which defeats the point of a short one. So: refresh while the employee is *active*, and once idle
 * past the configured timeout, stop and prompt for the password. The prompt is a dialog over the
 * page rather than a redirect, because a redirect discards the draft they were part-way through.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  changePassword as changePasswordCall,
  fetchAuthMode,
  fetchMe,
  login as loginCall,
  logout as logoutCall,
  refreshDelayMs,
  refreshSession,
} from "../api/auth";
import { ApiError } from "../api/client";
import type { AuthMode, Me, Role } from "../api/types";

export type SessionStatus = "loading" | "anonymous" | "authenticated" | "expired";

export interface SessionValue {
  status: SessionStatus;
  identity: Me | null;
  mode: AuthMode | null;
  /** Convenience for the navigation. `null` while loading, so a screen never guesses. */
  role: Role | null;
  /** True when the employee must change their password before anything else is usable. */
  mustChangePassword: boolean;
  signIn: (employeeId: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  changePassword: (current: string, replacement: string) => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside a SessionProvider");
  return context;
}

/**
 * How long without interaction before the session is treated as idle.
 *
 * Mirrors `session_idle_timeout_seconds` in `app/config.py`. Duplicated rather than fetched because
 * the client's copy is only an optimisation — it stops refreshing early so the employee gets a
 * prompt instead of a failed request. The server enforces the real timeout regardless of what this
 * says, which is why a stale copy here is harmless.
 */
export const IDLE_TIMEOUT_SECONDS = 1800;

/** Events that count as the employee being present. Passive listeners: none of them block. */
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "focus"] as const;

export interface SessionProviderProps {
  children: ReactNode;
  /** Overridable so a test does not have to wait half an hour. */
  idleTimeoutSeconds?: number;
}

export function SessionProvider({
  children,
  idleTimeoutSeconds = IDLE_TIMEOUT_SECONDS,
}: SessionProviderProps): JSX.Element {
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [identity, setIdentity] = useState<Me | null>(null);
  const [mode, setMode] = useState<AuthMode | null>(null);

  const lastActivity = useRef(Date.now());
  const refreshTimer = useRef<number | null>(null);

  const clearRefresh = useCallback(() => {
    if (refreshTimer.current !== null) {
      window.clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  /**
   * Schedule the next refresh, or let the session lapse.
   *
   * Recursive by design: each refresh reports its own lifetime, so a deployment that shortens the
   * access token takes effect on the next hop rather than on the next page load.
   */
  const scheduleRefresh = useCallback(
    (accessExpiresIn: number) => {
      clearRefresh();
      refreshTimer.current = window.setTimeout(() => {
        const idleFor = (Date.now() - lastActivity.current) / 1000;
        if (idleFor > idleTimeoutSeconds) {
          // Deliberately does not refresh. An idle session should end, and the employee should be
          // told rather than finding out from a 401 on their next click.
          setStatus("expired");
          return;
        }
        refreshSession()
          .then((session) => {
            setIdentity(session.identity);
            setStatus("authenticated");
            scheduleRefresh(session.access_expires_in);
          })
          .catch(() => {
            // Revoked, replayed, or the server restarted with a new signing key. All three mean
            // "sign in again", and none of them should look like a broken screen.
            setStatus("expired");
          });
      }, refreshDelayMs(accessExpiresIn));
    },
    [clearRefresh, idleTimeoutSeconds],
  );

  // Bootstrap. Asks the mode first, because a deployment behind an identity provider must not be
  // offered a password form, and `/auth/me` alone cannot tell the client which case it is in.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      let resolvedMode: AuthMode | null = null;
      try {
        resolvedMode = await fetchAuthMode();
      } catch {
        // An unreachable mode endpoint is not a reason to lock the employee out of a form they
        // may still be able to use. Fall back to offering one; the login attempt will say more.
        resolvedMode = { mode: "unknown", password_login: true };
      }
      if (cancelled) return;
      setMode(resolvedMode);

      try {
        const me = await fetchMe();
        if (cancelled) return;
        setIdentity(me);
        setStatus("authenticated");
        // No lifetime from `/me`, so refresh once soon to learn it. Cheap, and it also proves the
        // refresh cookie is still good before the employee starts work they could lose.
        scheduleRefresh(refreshMarginProbeSeconds());
      } catch (error) {
        if (cancelled) return;
        // 401 is the ordinary "not signed in" case and is not an error worth surfacing.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.warn("session bootstrap failed", error);
        }
        setStatus("anonymous");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [scheduleRefresh]);

  // Activity tracking. Only records a timestamp — no state update, so moving the pointer does not
  // re-render the application on every event.
  useEffect(() => {
    const note = () => {
      lastActivity.current = Date.now();
    };
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, note, { passive: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, note);
    };
  }, []);

  useEffect(() => clearRefresh, [clearRefresh]);

  const signIn = useCallback(
    async (employeeId: string, password: string) => {
      const session = await loginCall(employeeId, password);
      lastActivity.current = Date.now();
      setIdentity(session.identity);
      setStatus("authenticated");
      scheduleRefresh(session.access_expires_in);
    },
    [scheduleRefresh],
  );

  const signOut = useCallback(async () => {
    clearRefresh();
    try {
      await logoutCall();
    } catch {
      // Swallowed, not rethrown. Every caller is an `onClick`, so a rejection here has nobody to
      // handle it and surfaces as an unhandled rejection in the console instead of anything the
      // employee or an operator can act on. The cookie may well be gone server-side anyway.
    }
    // Cleared whether or not the server answered. A failed logout that left the screen looking
    // signed in reads as "it did not work", and the employee walks away from a session they
    // believe is closed.
    setIdentity(null);
    setStatus("anonymous");
  }, [clearRefresh]);

  const changePassword = useCallback(async (current: string, replacement: string) => {
    const me = await changePasswordCall(current, replacement);
    lastActivity.current = Date.now();
    setIdentity(me);
    setStatus("authenticated");
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      identity,
      mode,
      role: identity?.role ?? null,
      mustChangePassword: Boolean(identity?.must_change_password),
      signIn,
      signOut,
      changePassword,
    }),
    [status, identity, mode, signIn, signOut, changePassword],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/**
 * Lifetime assumed for the first refresh after a bootstrap from `/auth/me`, which does not report
 * one. Short on purpose: it is a probe, and the answer replaces it.
 */
function refreshMarginProbeSeconds(): number {
  return 120;
}
